import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Shield, Mail, ArrowLeft, AlertTriangle, Users } from 'lucide-react';
import { FiShield } from 'react-icons/fi';
import { useMsal, useAccount } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { AuthorizationResult } from '../../types/auth';
import { AZURE_AD_CONFIG } from '../../config/azureAdConfig';
import ChatAdminPanel from '../Chat/ChatAdminPanel';
import { userDbApi } from '../../services/userDbApi';

// Admin email list - must match backend list and Sidebar.tsx
const CHAT_ADMIN_EMAILS = [
  "paymanshu.sharma@maersk.com",
  "shantanu.rastogi@maersk.com",
  "srikant.yadav1@maersk.com",
  "krisha.joshi@maersk.com",
  "nikhil.nagesh@maersk.com"
]

interface UnauthorizedAccessProps {
  userEmail?: string;
  authorizationResult?: AuthorizationResult;
}

export const UnauthorizedAccess: React.FC<UnauthorizedAccessProps> = ({ userEmail, authorizationResult }) => {
  const { accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const navigate = useNavigate();
  
  // Admin panel state
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  
  // Waitlist state
  const [isJoiningWaitlist, setIsJoiningWaitlist] = useState(false);
  const [hasJoinedWaitlist, setHasJoinedWaitlist] = useState(false);
  
  // Check if current user is admin (same logic as Sidebar.tsx)
  // IMPORTANT: Prioritize userEmail prop over account.username since it's already extracted in AuthorizedRoute
  const isAdmin = useMemo(() => {
    const currentUserEmail = userEmail || account?.username || '';
    return CHAT_ADMIN_EMAILS.includes(currentUserEmail.toLowerCase());
  }, [userEmail, account]);

  const handleBackToLanding = () => {
    navigate('/');
  };

  const handleJoinWaitlist = async () => {
    if (!userEmail) {
      alert('Unable to add to waitlist - email not found');
      return;
    }

    setIsJoiningWaitlist(true);
    try {
      const displayName = account?.name || '';
      await userDbApi.joinWaitlist(userEmail, displayName);
      setHasJoinedWaitlist(true);
    } catch (error) {
      console.error('Failed to join waitlist:', error);
      alert('Failed to join waitlist. Please try again or contact support.');
    } finally {
      setIsJoiningWaitlist(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center relative">
      {/* Floating Back Button - Top Left */}
      <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.3 }}
        onClick={handleBackToLanding}
        className="fixed top-6 left-6 z-50 bg-gray-800 hover:bg-gray-700 text-white p-3 rounded-full shadow-lg transition-colors border border-gray-700 hover:border-gray-600"
        title="Back to Landing Page"
      >
        <ArrowLeft size={20} />
      </motion.button>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-md w-full mx-auto px-6"
      >
        <div className="bg-gray-800 rounded-2xl shadow-2xl p-8 border border-red-500/20 text-center">
          <div className="flex justify-center mb-6">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.3, type: "spring" }}
              className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center"
            >
              {AZURE_AD_CONFIG.USE_GROUP_BASED_AUTH ? (
                <Users size={32} className="text-red-400" />
              ) : (
                <Shield size={32} className="text-red-400" />
              )}
            </motion.div>
          </div>
          
          <motion.h1
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-2xl font-bold text-white mb-4"
          >
            Access Restricted
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-gray-300 mb-6"
          >
            Your account is not authorized to access ShipmentCoCaptain.
          </motion.p>
          
          {userEmail && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ 
                opacity: 1, 
                y: 0,
                backgroundColor: hasJoinedWaitlist ? 'rgba(34, 197, 94, 0.1)' : 'rgba(55, 65, 81, 0.5)',
                borderColor: hasJoinedWaitlist ? 'rgba(34, 197, 94, 0.3)' : 'transparent'
              }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="rounded-lg p-4 mb-6 border"
            >
              <div className="flex items-center justify-center gap-2">
                {hasJoinedWaitlist ? (
                  <>
                    <div className="text-green-400 font-semibold">✓</div>
                    <Mail size={16} className="text-green-400" />
                    <span className="text-sm font-mono text-green-400">{userEmail}</span>
                  </>
                ) : (
                  <>
                    <Mail size={16} className="text-gray-400" />
                    <span className="text-sm font-mono text-gray-400">{userEmail}</span>
                  </>
                )}
              </div>
              
              {hasJoinedWaitlist && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  transition={{ delay: 0.3 }}
                  className="mt-2 text-xs text-center text-green-300"
                >
                  You're on the waitlist! We'll reach out soon.
                </motion.div>
              )}
            </motion.div>
          )}
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="space-y-4"
          >
            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
              <div className="flex items-center gap-2 text-yellow-400 mb-2">
                <AlertTriangle size={16} />
                <span className="text-sm font-medium">What to do?</span>
              </div>
              <p className="text-xs text-yellow-300">
                Please reach out to shantanu.rastogi@maersk.com if you're interested in trying out Shipment CoCaptain!
              </p>
            </div>
            
            {/* Admin Panel Button - Only visible to admins */}
            {isAdmin && (
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowAdminPanel(true)}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
              >
                <FiShield size={18} />
                Open Admin Panel
              </motion.button>
            )}
            
            {/* Join Waitlist Button */}
            {!hasJoinedWaitlist && (
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleJoinWaitlist}
                disabled={isJoiningWaitlist}
                className="w-full bg-primary hover:bg-primary-dark text-white py-3 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isJoiningWaitlist ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Joining Waitlist...
                  </>
                ) : (
                  <>
                    <Mail size={18} />
                    Join Waitlist
                  </>
                )}
              </motion.button>
            )}
          </motion.div>
        </div>
      </motion.div>
      
      {/* Chat Admin Panel - Only for admins */}
      {isAdmin && showAdminPanel && (
        <ChatAdminPanel
          adminEmail={account?.username || userEmail || ''}
          onClose={() => setShowAdminPanel(false)}
        />
      )}
    </div>
  );
};

export default UnauthorizedAccess;
